import { useCallback, useState } from 'react';

import { useStoreContext } from 'features/store';
import { DEFAULT_SEARCH_RADIUS_IN_METERS } from 'features/store/constants';
import { buildServiceModeStatusesForStore } from 'features/store/hooks/use-store-availability-fns';
import { useNearbyRestaurants } from 'features/store/pages/store-locator/hooks/helpers/use-restaurants';
import { ISearchNearbyRestaurants } from 'features/store/pages/store-locator/hooks/types';

import { IRestaurant } from '@rbi-ctg/store';
import { FilterRestaurantType, OperationalStatus, ServiceMode } from 'generated/graphql-gateway';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isValidPosition } from 'utils/geolocation';

import { useSavedRecommendedStores } from './use-saved-recommended-stores';

export const useFindAvailablePickupRestaurant = () => {
  const { alreadySelectedRecommendedStore } = useSavedRecommendedStores();

  const { fetch: fetchNearbyRestaurants } = useNearbyRestaurants();
  const { loadCurrentPositionAsync } = useGeolocation();
  const { store: closedStore } = useStoreContext();

  const [isLoading, setIsLoading] = useState(false);

  const [availableRestaurant, setAvailableRestaurant] = useState<IRestaurant | null>(null);

  const defaultDiagonal =
    useFlag(LaunchDarklyFlag.RADIUS_FOR_STORE_LOCATOR) || DEFAULT_SEARCH_RADIUS_IN_METERS;

  // TODO: Unify restaurant search functionality across the codebase
  const searchNearbyRestaurantsAsync = useCallback(
    async ({ location }: ISearchNearbyRestaurants): Promise<IRestaurant[]> => {
      if (!closedStore.number && !isValidPosition(location)) {
        return [];
      }

      const nearbyRestaurants = ((await fetchNearbyRestaurants(
        {
          coordinates: {
            userLat: Number(location.lat),
            userLng: Number(location.lng),
            searchRadius: defaultDiagonal,
          },
          filter: FilterRestaurantType.NEARBY,
          status: OperationalStatus.OPEN,
        },
        { allowFromCache: false }
      )) as unknown) as IRestaurant[];

      return nearbyRestaurants ?? [];
    },
    [closedStore.number, defaultDiagonal, fetchNearbyRestaurants]
  );

  const clearSearchState = () => {
    setIsLoading(false);
    setAvailableRestaurant(null);
  };

  // This function cannot evaluate if the restaurant is available for pickup using function that use launch darkly flags
  // because it does not take into account the state of the launch darkly flag when iterating.
  // TODO: Refactor this function to take into account the state of the launch darkly flag when iterating.
  const findFirstOrderableRestaurantForPickUp = async () => {
    const userCoordinates = await loadCurrentPositionAsync();

    if (!userCoordinates) {
      clearSearchState();
      return;
    }

    try {
      setIsLoading(true);
      setAvailableRestaurant(null);
      const nearbyRestaurants = await searchNearbyRestaurantsAsync({
        location: userCoordinates,
      });

      if (!nearbyRestaurants.length) {
        clearSearchState();
        return;
      }

      for (const restaurant of nearbyRestaurants) {
        const isAlreadySelectedRecommendedStore =
          restaurant._id && alreadySelectedRecommendedStore.get(restaurant._id);

        if (restaurant._id === closedStore._id || isAlreadySelectedRecommendedStore) {
          continue;
        }

        // Since we are not using launch darkly flags, we need to build the service mode statuses for the restaurant
        // and check if the restaurant is available for pickup using the service mode statuses.
        const servicesModesPickup = {
          [ServiceMode.CURBSIDE]: true,
          [ServiceMode.DELIVERY]: true,
          [ServiceMode.DRIVE_THRU]: true,
          [ServiceMode.EAT_IN]: true,
          [ServiceMode.MOBILE_ORDER_DRIVE_THRU]: true,
          [ServiceMode.TAKEOUT]: true,
        };

        const serviceModeStatuses = buildServiceModeStatusesForStore(
          restaurant,
          restaurant.localTime,
          servicesModesPickup
        ).filter(status => status.serviceMode !== ServiceMode.DELIVERY);

        if (Object.values(serviceModeStatuses).some(status => status.available)) {
          setAvailableRestaurant(restaurant);
          return;
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    findFirstOrderableRestaurantForPickUp,
    isLoading,
    availableRestaurant,
    clearSearchState,
  };
};
